import { useState, useMemo, useEffect, useRef } from "react";
import { Link as RemixLink } from "@remix-run/react";
import {
  addMonths,
  eachDayOfInterval,
  startOfMonth,
  endOfMonth,
  format,
} from "date-fns";
import { DayPicker } from "react-day-picker";
import { useQueries } from "@tanstack/react-query";

import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Text,
  VStack,
  Tag,
  TagLabel,
  TagCloseButton,
  Tabs,
  TabList,
  Tab,
} from "@chakra-ui/react";

import { ArrowForwardIcon, WarningIcon } from "@chakra-ui/icons";

import { TapTooltip } from "#app/components/TapTooltip.jsx";
import { GlowFrame } from "./GlowFrame.jsx";
import { formatTagDate } from "#app/utils/dateFormat.js";
import { useLocale } from "#app/context/LocaleContext.jsx";
import {
  buildQueryDescriptors,
  buildCtaUrl,
  normalizeActiveGroup,
} from "./availabilityCalendarHelpers.js";
import { getRequestMaxDate } from "#app/config/bookingWindow.js";

const MAX_DATE = endOfMonth(addMonths(new Date(), 10));

async function fetchAvailability({ facilityId, from, to, siteIds }) {
  let url = `/api/availability?facilityId=${facilityId}&from=${from}&to=${to}`;
  if (siteIds && siteIds.length > 0) {
    url += `&siteIds=${siteIds.join(",")}`;
  }
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Availability fetch failed: ${res.status}`);
  return res.json();
}

export function AvailabilityCalendar({
  facilityId,
  facilitySlug,
  facilityName,
  facilities,
  activeFacilityIndex,
  onFacilityChange,
  siteGroups,
}) {
  if (
    typeof process !== "undefined" &&
    process.env.NODE_ENV !== "production" &&
    siteGroups &&
    facilities
  ) {
    throw new Error(
      "AvailabilityCalendar: siteGroups + facilities are mutually exclusive in v1.",
    );
  }

  const locale = useLocale();
  const [selectedDates, setSelectedDates] = useState([]);
  const [displayedMonth, setDisplayedMonth] = useState(new Date());
  const [userHasNavigated, setUserHasNavigated] = useState(false);
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const hasBeenActive = useRef(new Set([0]));
  const lastFacilityId = useRef(facilityId);
  const nextMonth = addMonths(displayedMonth, 1);

  if (facilityId !== lastFacilityId.current) {
    lastFacilityId.current = facilityId;
    setUserHasNavigated(false);
    setDisplayedMonth(new Date());
    setSelectedDates([]);
    setActiveGroupIndex(0);
    hasBeenActive.current = new Set([0]);
  }

  const now = new Date();
  const fromDate = format(startOfMonth(now), "yyyy-MM-dd");
  const toDate = format(endOfMonth(addMonths(now, 10)), "yyyy-MM-dd");

  // How far ahead a guest may *select* dates. Extended-window facilities (e.g.
  // Surf Grove) reach further out than the default MAX_DATE. Note we keep the
  // availability *fetch* window (`toDate`, above) at the default ~10 months so
  // it stays within the /api/availability range cap — far-future dates simply
  // render as "unknown" until the scanner covers them, which is fine for a
  // waitlist signup.
  const pickerMaxDate = getRequestMaxDate(facilitySlug, { fallback: MAX_DATE });

  const queryDescriptors = useMemo(
    () => buildQueryDescriptors(siteGroups),
    [siteGroups],
  );

  const queries = useQueries({
    queries: queryDescriptors.map((descriptor, i) => ({
      queryKey: descriptor.seedKeys
        ? [
            "FacilityAvailability",
            facilityId,
            fromDate,
            toDate,
            "seedKeys",
            descriptor.seedKeys.join(","),
          ]
        : ["FacilityAvailability", facilityId, fromDate, toDate],
      queryFn: () =>
        fetchAvailability({
          facilityId,
          from: fromDate,
          to: toDate,
          siteIds: descriptor.siteIds,
        }),
      staleTime: 60_000,
      enabled:
        !!facilityId &&
        (i === activeGroupIndex || hasBeenActive.current.has(i)),
    })),
  });

  // Mutate the ref BEFORE setState so the post-state-update render sees the
  // just-mutated set. Critical for `useQueries` `enabled` re-evaluation.
  const handleSiteGroupChange = (nextIndex) => {
    hasBeenActive.current.add(nextIndex);
    setActiveGroupIndex(nextIndex);
  };

  const activeQuery = queries[activeGroupIndex] ?? queries[0];
  const allData = activeQuery?.data;

  useEffect(() => {
    if (!allData || userHasNavigated) return;
    for (const dateStr of Object.keys(allData).sort()) {
      if (allData[dateStr].total > 0) {
        setDisplayedMonth(startOfMonth(new Date(dateStr + "T00:00:00")));
        return;
      }
    }
  }, [allData, userHasNavigated]);

  const handleMonthChange = (month) => {
    setUserHasNavigated(true);
    setDisplayedMonth(month);
  };

  const { availableDays, unavailableDays, unknownDays, loadingDays } =
    useMemo(() => {
      const available = [];
      const unavailable = [];
      const unknown = [];
      const loading = [];
      const knownDates = new Set();

      const month1Start = format(startOfMonth(displayedMonth), "yyyy-MM-dd");
      const month2End = format(endOfMonth(nextMonth), "yyyy-MM-dd");

      if (allData) {
        for (const [dateStr, info] of Object.entries(allData)) {
          if (dateStr < month1Start || dateStr > month2End) continue;
          const d = new Date(dateStr + "T00:00:00");
          if (isNaN(d.getTime())) continue;
          knownDates.add(dateStr);
          if (info.total === 0) {
            unknown.push(d);
          } else if (info.available > 0) {
            available.push(d);
          } else {
            unavailable.push(d);
          }
        }
      }

      for (const month of [displayedMonth, nextMonth]) {
        const start = startOfMonth(month);
        const end = endOfMonth(month);
        for (const day of eachDayOfInterval({ start, end })) {
          const dateStr = format(day, "yyyy-MM-dd");
          if (!knownDates.has(dateStr)) {
            loading.push(day);
          }
        }
      }

      return {
        availableDays: available,
        unavailableDays: unavailable,
        unknownDays: unknown,
        loadingDays: loading,
      };
    }, [allData, displayedMonth, nextMonth]);

  const activeGroup = siteGroups
    ? normalizeActiveGroup(activeGroupIndex, siteGroups)
    : null;
  const ctaUrl = buildCtaUrl({ facilitySlug, selectedDates, activeGroup });

  const removeDay = (day) => {
    setSelectedDates((prev) =>
      prev.filter((d) => d.getTime() !== day.getTime()),
    );
  };

  const siteGroupTabs =
    siteGroups && siteGroups.length > 0
      ? [
          { label: "All sites", siteCount: null, isError: false },
          ...siteGroups.map((group, i) => ({
            label: group.name,
            siteCount: group.siteCount,
            isError: queries[i + 1]?.isError ?? false,
          })),
        ]
      : null;

  return (
    <GlowFrame
      id="calendar"
      w="100%"
      maxW={{ base: "100%", md: "44rem" }}
      mx="auto"
      sx={{ scrollMarginTop: "80px" }}
      innerProps={{ py: { base: 7, md: 9 }, px: { base: 4, md: 6 } }}
    >
      <Box textAlign="center" mb={{ base: 5, md: 7 }}>
        <Heading
          as="h2"
          size={{ base: "xs", md: "sm" }}
          fontWeight="extrabold"
          mb={3}
        >
          Sold out doesn&rsquo;t mean forever
        </Heading>
        <Text color="muted" fontSize={{ base: "sm", md: "md" }}>
          Cancellations happen all season long. Pick your dates and we&rsquo;ll
          watch for openings{facilityName && ` at ${facilityName}`}
        </Text>
      </Box>

      {facilities && (
        <Tabs
          index={activeFacilityIndex}
          onChange={onFacilityChange}
          variant="line"
          colorScheme="brand"
          mb={{ base: 4, md: 6 }}
        >
          <TabList justifyContent="center" flexWrap="wrap" borderBottom="none">
            {facilities.map((f) => (
              <Tab key={f.slug} fontSize="sm" fontWeight="medium">
                {f.name}
              </Tab>
            ))}
          </TabList>
        </Tabs>
      )}

      {siteGroupTabs && (
        <Tabs
          index={activeGroupIndex}
          onChange={handleSiteGroupChange}
          variant="solid-rounded"
          colorScheme="brand"
          mb={{ base: 4, md: 6 }}
        >
          <TabList
            justifyContent="flex-start"
            flexWrap="nowrap"
            overflowX="auto"
            mx={{ base: -4, md: -6 }}
            px={{ base: 4, md: 6 }}
            gap={2}
            sx={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitOverflowScrolling: "touch",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {siteGroupTabs.map((tab) => (
              <Tab
                key={tab.label}
                fontSize="sm"
                fontWeight="medium"
                minH="40px"
                px={4}
                flexShrink={0}
                whiteSpace="nowrap"
                bg="bg-muted"
                color="default"
                borderWidth="1px"
                borderColor="border"
                _hover={{ bg: "bg-emphasized" }}
                _selected={{
                  bg: "brand.500",
                  color: "white",
                  borderColor: "brand.500",
                }}
              >
                {tab.label}
                {tab.siteCount != null && (
                  <Text
                    as="span"
                    ml={1}
                    opacity={0.7}
                    display={{ base: "none", sm: "inline" }}
                  >
                    ({tab.siteCount})
                  </Text>
                )}
                {tab.isError && (
                  <Box
                    as={WarningIcon}
                    ml={1}
                    boxSize="12px"
                    color="warning"
                    aria-label={`Couldn't load ${tab.label}`}
                  />
                )}
              </Tab>
            ))}
          </TabList>
        </Tabs>
      )}

      <HStack spacing={4} mb={2} fontSize="xs" color="subtle" justify="center">
        <HStack spacing={1}>
          <Box w="6px" h="6px" borderRadius="full" bg="green.400" />
          <Text>Available</Text>
        </HStack>
        <HStack spacing={1}>
          <Box w="6px" h="6px" borderRadius="full" bg="red.400" />
          <Text>Fully booked</Text>
        </HStack>
        <HStack spacing={1}>
          <Box w="6px" h="6px" borderRadius="full" bg="gray.300" />
          <TapTooltip
            label="We only know about dates people are watching. This means nobody has requested this date yet, or the campground isn't open."
            fontSize="xs"
            maxW="220px"
            placement="bottom"
          >
            Unknown
          </TapTooltip>
        </HStack>
      </HStack>

      {/* Desktop: 2-month calendar */}
      <Box display={{ base: "none", md: "flex" }} justifyContent="center">
        <Box
          className="rdp-availability-cal"
          as={DayPicker}
          mode="multiple"
          min={0}
          month={displayedMonth}
          selected={selectedDates}
          onSelect={(dates) => setSelectedDates(dates || [])}
          onMonthChange={handleMonthChange}
          numberOfMonths={2}
          navLayout="around"
          startMonth={now}
          endMonth={pickerMaxDate}
          disabled={[{ before: now }, { after: pickerMaxDate }]}
          modifiers={{
            available: availableDays,
            unavailable: unavailableDays,
            unknown: unknownDays,
            loading: loadingDays,
          }}
          modifiersClassNames={{
            available: "rdp-day_available",
            unavailable: "rdp-day_unavailable",
            unknown: "rdp-day_unknown",
            loading: "rdp-day_unknown",
          }}
        />
      </Box>
      {/* Mobile: 1-month calendar */}
      <Box display={{ base: "flex", md: "none" }} justifyContent="center">
        <Box
          className="rdp-availability-cal"
          as={DayPicker}
          mode="multiple"
          min={0}
          month={displayedMonth}
          selected={selectedDates}
          onSelect={(dates) => setSelectedDates(dates || [])}
          onMonthChange={handleMonthChange}
          startMonth={now}
          endMonth={pickerMaxDate}
          disabled={[{ before: now }, { after: pickerMaxDate }]}
          modifiers={{
            available: availableDays,
            unavailable: unavailableDays,
            unknown: unknownDays,
            loading: loadingDays,
          }}
          modifiersClassNames={{
            available: "rdp-day_available",
            unavailable: "rdp-day_unavailable",
            unknown: "rdp-day_unknown",
            loading: "rdp-day_unknown",
          }}
        />
      </Box>

      {selectedDates.length > 0 && (
        <VStack spacing={3} mt={4}>
          <Flex
            w="100%"
            overflowX="auto"
            gap={2}
            py={2}
            mx={{ base: -4, md: 0 }}
            px={{ base: 4, md: 0 }}
            justify={{ base: "flex-start", md: "center" }}
            sx={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitOverflowScrolling: "touch",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {[...selectedDates]
              .sort((a, b) => a - b)
              .map((day) => (
                <Tag size="md" flexShrink={0} key={day.toISOString()}>
                  <TagLabel>{formatTagDate(day, locale)}</TagLabel>
                  <TagCloseButton onClick={() => removeDay(day)} />
                </Tag>
              ))}
          </Flex>
          <Button
            variant="primary"
            size="lg"
            fontWeight="bold"
            as={RemixLink}
            to={ctaUrl}
            rightIcon={<ArrowForwardIcon />}
          >
            Watch {selectedDates.length === 1 ? "this date" : "these dates"}
          </Button>
        </VStack>
      )}

      {selectedDates.length === 0 && (
        <Box textAlign="center" mt={4}>
          <Text color="muted" fontSize="sm" mb={3}>
            Tap any date to get started
          </Text>
          <Button
            variant="secondary"
            size="md"
            as={RemixLink}
            to={ctaUrl}
            rightIcon={<ArrowForwardIcon />}
          >
            Or skip ahead
          </Button>
        </Box>
      )}
    </GlowFrame>
  );
}
